"use client";

import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useEffect, useRef } from "react";

import type { Outcome } from "@/lib/quiz-types";

import { screenVariants } from "./screen-variants";

interface OutcomeScreenProps {
  outcome: Outcome;
  onRestart: () => void;
}

export function OutcomeScreen({ outcome, onRestart }: OutcomeScreenProps) {
  const ctaRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    ctaRef.current?.focus();
  }, [outcome.id]);

  const isPrimary = outcome.cta.variant === "primary";

  return (
    <motion.div
      key={outcome.id}
      variants={screenVariants}
      initial="enter"
      animate="center"
      exit="exit"
      className="flex flex-col gap-5"
    >
      <p className="text-label-10 lg:text-label-12 pb-3 text-white uppercase opacity-40">
        Your result
      </p>

      <div className="border-t border-white/10 pt-5">
        <h1 className="text-heading-20 lg:text-heading-24 text-white">
          {outcome.headline}
        </h1>
        <p className="text-body mt-3 text-white/60">{outcome.subtitle}</p>
      </div>

      <div className="flex flex-col items-start gap-4 border-t border-white/10 pt-5">
        <a
          ref={ctaRef}
          href={outcome.cta.href}
          className={[
            "relative inline-flex h-14 w-full items-center justify-center gap-2 overflow-hidden rounded-[14px] px-6",
            "text-base font-semibold transition-colors",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black",
            "lg:w-auto lg:justify-start",
            isPrimary
              ? "bg-pulse-700 text-white after:absolute after:inset-0 after:block after:h-full after:w-full after:rounded-[14px] after:transition-colors hover:after:bg-white/20"
              : "border border-white/20 bg-transparent text-white hover:border-white/40",
          ].join(" ")}
        >
          {/* TODO: track event cta_clicked */}
          {outcome.cta.label}
          <ArrowRight className="h-4 w-4 shrink-0" />
        </a>

        <button
          type="button"
          onClick={() => {
            // TODO: track event quiz_restarted
            onRestart();
          }}
          className="text-body-sm focus-visible:ring-pulse-500 font-medium text-white/40 underline underline-offset-4 hover:text-white/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
        >
          Start over
        </button>
      </div>
    </motion.div>
  );
}
